import { useState, useEffect } from "react";
import type { SearchOption } from "../Map/TransitOverlay/MapSearchInput";
import type { Stop } from "../../types";
import { findStops, type StopFindResult } from "../../api/stopFind";
import { isPureNartrafikFromStop } from "../../utils/stopType";

function stripStopName(name: string) {
  return name.replace(/\s*[\(\[]?[A-ZÅÄÖ][\)\]]?$/, "").trim();
}

function getRootIdFromStop(stop: Stop): string {
  return String(stop.id).split("-")[0];
}

function toRad(deg: number) {
  return (deg * Math.PI) / 180;
}

function haversineMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
) {
  const R = 6371000; // meter
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const la1 = toRad(aLat);
  const la2 = toRad(bLat);

  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
  return R * c;
}

function matchFindHitForGroup(
  group: Stop[],
  hits: StopFindResult[]
): StopFindResult | null {
  if (!group.length || !hits.length) return null;

  const baseName = stripStopName(group[0].name).toLowerCase();

  const nameCandidates = hits.filter((h) => {
    const place = (h.PlaceName || "").toLowerCase();
    return place === baseName || place.startsWith(baseName + ",");
  });

  const candidates = nameCandidates.length ? nameCandidates : hits;
  if (!candidates.length) return null;

  const distances: { hit: StopFindResult; dist: number }[] = [];

  for (const hit of candidates) {
    const [hitLat, hitLng] = hit.Ll;
    let minForHit = Infinity;
    for (const s of group) {
      const d = haversineMeters(s.lat, s.lng, hitLat, hitLng);
      if (d < minForHit) minForHit = d;
    }
    distances.push({ hit, dist: minForHit });
  }

  distances.sort((a, b) => a.dist - b.dist);
  const best = distances[0];
  const second = distances[1];

  const MAX_ACCEPT_DIST = 80; // m
  const MIN_GAP = 50; // m

  if (!best || best.dist > MAX_ACCEPT_DIST) return null;
  if (second && second.dist - best.dist < MIN_GAP) return null;

  return best.hit;
}

function enrichGroupWithCity(
  group: Stop[],
  hit: StopFindResult | null
): Stop[] {
  const cityName = hit?.TaxeZone?.Name?.trim();

  if (!cityName) return group;

  return group.map((stop) => ({
    ...stop,
    cityName,
    taxeZoneName: cityName,
  }));
}

export function useStopSearch(
  allStops: Stop[],
  onStopSelect: (stop: Stop) => void,
  includeNartrafik: boolean = false
) {
  const [query, setQuery] = useState("");
  const [options, setOptions] = useState<(SearchOption & { data?: Stop[] })[]>(
    []
  );
  const [findHits, setFindHits] = useState<StopFindResult[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.length < 2) {
      setFindHits([]);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const hits = await findStops(query, "stop");
        if (!cancelled) {
          setFindHits(hits);
        }
      } catch (e) {
        console.error("findStops failed", e);
        if (!cancelled) setFindHits([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => {
    if (query.length < 2) {
      setOptions([]);
      return;
    }

    const qLower = query.trim().toLowerCase();

    const groups = Object.values(
      allStops
        .filter((stop) =>
          stripStopName(stop.name).toLowerCase().includes(qLower)
        )
        .reduce<Record<string, Stop[]>>((acc, stop) => {
          const baseName = stripStopName(stop.name);
          const rootId = getRootIdFromStop(stop);

          const key = `${baseName}#${rootId}`;
          (acc[key] = acc[key] || []).push(stop);
          return acc;
        }, {})
    );

    const visibleGroups = includeNartrafik
      ? groups
      : groups.filter((group) => !group.every(isPureNartrafikFromStop));

    const nextOptions = visibleGroups.map((group) => {
      const baseName = stripStopName(group[0].name);

      const hit = matchFindHitForGroup(group, findHits);
      const prettyName = hit ? hit.PlaceName : baseName;
      const enrichedGroup = enrichGroupWithCity(group, hit);

      return {
        id: group[0].id,
        label: prettyName,
        value: baseName,
        data: enrichedGroup,
      } as SearchOption & { data: Stop[] };
    });

    nextOptions.sort((a, b) => {
      const q = qLower;

      const score = (label: string) => {
        const l = label.toLowerCase();

        // Ecact match
        if (l === q) return 100;

        // beginning of label match
        if (l.startsWith(q)) return 80;

        // some word starts with query
        if (l.split(/\s+/).some((w) => w.startsWith(q))) return 60;

        // contains query somewhere
        if (l.includes(q)) return 40;

        return 0;
      };

      const scoreDiff = score(b.label) - score(a.label);

      if (scoreDiff !== 0) return scoreDiff;

      // alfabetical fallback
      return a.label.localeCompare(b.label, "sv");
    });

    setOptions(nextOptions);
  }, [query, allStops, findHits, includeNartrafik]);

  const handleInputChange = (value: string) => {
    setQuery(value);
  };

  const handleSelect = (
    option: SearchOption & {
      data?: Stop[];
    }
  ) => {
    const group = option.data ?? [];

    const representativeStop = group[0];

    if (representativeStop) {
      onStopSelect(representativeStop);
    }

    setOptions([]);
    setQuery("");
  };

  return { query, options, handleInputChange, handleSelect, loading };
}
